import React, { useState, useCallback, useMemo, createContext, useContext } from 'react';
import ReactDOM from 'react-dom';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { Table } from '../../../../components';
import classNames from 'classnames';

import './BaseDiscoveryTable.scss';
import { formatCount } from '../../helpers';

// Passes column-reordering config into the table header wrapper without closures.
const ColumnReorderingCtx = createContext(null);

function ColumnReorderingThead({ children, ...theadProps }) {
    const ctx = useContext(ColumnReorderingCtx);
    const [activeColumn, setActiveColumn] = useState(null);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragStart = useCallback(
        (event) => {
            if (!ctx) return;
            const col = ctx.visibleColumns?.find((c) => c.key === event.active.id);
            setActiveColumn(col ?? null);
        },
        [ctx],
    );

    const handleDragEnd = useCallback(
        (event) => {
            ctx?.onColumnDragEnd(event);
            setActiveColumn(null);
        },
        [ctx],
    );

    if (!ctx) {
        // Fallback to render non-draggable headers if context is missing
        return <thead {...theadProps}>{children}</thead>;
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToHorizontalAxis]}
            accessibility={{ container: typeof document !== 'undefined' ? document.body : undefined }}
        >
            <SortableContext items={ctx.columnOrder} strategy={horizontalListSortingStrategy}>
                <thead {...theadProps}>{children}</thead>
            </SortableContext>
            {/* Separate drag overlay in DOM to avoid conflicting drag overlays (causes console warnings without) */}
            {typeof document !== 'undefined' &&
                ReactDOM.createPortal(
                    <DragOverlay>
                        {activeColumn ? <div className="column-drag-overlay">{activeColumn.title}</div> : null}
                    </DragOverlay>,
                    document.body,
                )}
        </DndContext>
    );
}

/**
 * BaseDiscoveryTable - A shared table component for Discovery pages
 * Used by both Inventory and MoveGroupsAndApps
 */
export const BaseDiscoveryTable = ({
    tableData,
    isLoading,
    isFetching,
    pagination,
    handlePaginationChange,
    onRowClick,
    selectedRowData,
    rowSelection,
    visibleColumns,
    columnOrder,
    handleDragEnd,
    handleSortingUpdate = () => {},
    DraggableColumnHeader,
    tableType = null,
    disableSorting = false,
    customComponents = {},
    enableRowDragging = false,
}) => {
    // Stable context value — only recreated when column order or drag handler changes.
    const columnReorderingCtxValue = useMemo(
        () => ({
            columnOrder,
            onColumnDragEnd: handleDragEnd,
            visibleColumns,
        }),
        [columnOrder, handleDragEnd, visibleColumns],
    );

    const tableClassNames = classNames('base-discovery-table-container', {
        'base-discovery-table-container--inventory': tableType === 'inventory',
    });

    const draggableColumns = useMemo(() => {
        return visibleColumns.map((col) => {
            const column = {
                ...col,
                onHeaderCell: (column) => {
                    const originalProps = col.onHeaderCell ? col.onHeaderCell(column) : {};
                    return { ...originalProps, id: col.key };
                },
            };
            if (disableSorting && column.sorter) {
                delete column.sorter;
                delete column.sortOrder;
            }
            return column;
        });
    }, [visibleColumns, disableSorting]);

    return (
        <div className={tableClassNames}>
            {/* ColumnReorderingCtx provides column drag-and-drop config to the headers*/}
            <ColumnReorderingCtx.Provider value={columnReorderingCtxValue}>
                <Table
                    className="base-discovery-table"
                    size="small"
                    rowKey={(record) => record.key || record.id}
                    columns={draggableColumns}
                    dataSource={tableData}
                    loading={isLoading || isFetching}
                    rowSelection={rowSelection}
                    scroll={{ x: 2000 }}
                    onChange={disableSorting ? undefined : handleSortingUpdate}
                    onRow={(record) => ({
                        onClick: () => onRowClick?.(record),
                        style: { cursor: 'pointer' },
                        className:
                            selectedRowData && (record.key || record.id) === (selectedRowData.key || selectedRowData.id)
                                ? 'selected-row'
                                : '',
                    })}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showQuickJumper: false,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        onChange: handlePaginationChange,
                        onShowSizeChange: handlePaginationChange,
                        showTotal: (total, range) => `Showing ${formatCount(range[0])} to ${formatCount(range[1])} of ${formatCount(total)} entries`,
                    }}
                    components={{
                        header: {
                            wrapper: ColumnReorderingThead,
                            cell: DraggableColumnHeader,
                        },
                        ...customComponents,
                    }}
                />
            </ColumnReorderingCtx.Provider>
        </div>
    );
};
